/**
 * Hook that calls the `resolveCategoryConflicts` mutation to mark every
 * `isConflict: true` line item in a single category of a contract
 * extraction as resolved (server flips the flag to `false` atomically
 * and records a single Activity row with the resolved count).
 *
 * After a successful resolution the hook refetches the
 * `GetContractExtraction` query so the rendered view picks up the
 * updated flags without a full drawer reload. Durable, org-visible
 * resolution state is maintained by the backend — this hook does not
 * touch AsyncStorage.
 *
 * The GraphQL document is defined inline via `gql` (same convention
 * used by the other extraction hooks in this feature) so it can ship
 * before the codegen step runs against the updated schema.
 * @module features/contracts/hooks/useResolveCategoryConflicts
 */

import { gql, useMutation } from "@apollo/client";
import * as Sentry from "@sentry/react-native";
import { useCallback, useMemo, useState } from "react";

import { activeContractExtractionRefetch } from "./contractExtractionRefetch";

/** GraphQL document for the ResolveCategoryConflicts mutation. */
export const RESOLVE_CATEGORY_CONFLICTS_MUTATION = gql`
  mutation ResolveCategoryConflicts($input: ResolveCategoryConflictsInput!) {
    resolveCategoryConflicts(input: $input) {
      success
    }
  }
`;

/** Shape of the ResolveCategoryConflicts mutation response. */
interface ResolveCategoryConflictsResponse {
  readonly resolveCategoryConflicts: { readonly success: boolean };
}

/** Shape of the ResolveCategoryConflicts mutation variables. */
interface ResolveCategoryConflictsVariables {
  readonly input: {
    readonly extractionId: string;
    readonly category: string;
  };
}

/** Public API returned by `useResolveCategoryConflicts`. */
export interface UseResolveCategoryConflictsResult {
  /**
   * Calls the mutation for a single category. Returns `true` when the
   * server confirms success, `false` on any error. Refetches the
   * `GetContractExtraction` query on success so the UI updates.
   * @param extractionId - UUID of the extraction whose category to resolve
   * @param category - Category key within `extractedData`
   * @param playerId - Player UUID (needed for the extraction query refetch variables)
   * @param documentType - Document type (needed for the extraction query refetch variables)
   */
  readonly resolveCategoryConflicts: (
    extractionId: string,
    category: string,
    playerId: string,
    documentType: string
  ) => Promise<boolean>;
  /** Whether a resolve request is currently in flight. */
  readonly isResolving: boolean;
}

/**
 * Wraps the ResolveCategoryConflicts mutation with loading state and
 * automatic refetching of the extraction query on success.
 * @returns Imperative resolve function + loading state
 */
export function useResolveCategoryConflicts(): UseResolveCategoryConflictsResult {
  const [runMutation] = useMutation<
    ResolveCategoryConflictsResponse,
    ResolveCategoryConflictsVariables
  >(RESOLVE_CATEGORY_CONFLICTS_MUTATION);

  const [isResolving, setIsResolving] = useState(false);

  const resolveCategoryConflicts = useCallback(
    async (
      extractionId: string,
      category: string,
      playerId: string,
      documentType: string
    ): Promise<boolean> => {
      setIsResolving(true);
      try {
        const { data } = await runMutation({
          variables: { input: { extractionId, category } },
          refetchQueries: [
            activeContractExtractionRefetch(playerId, documentType),
          ],
          awaitRefetchQueries: true,
        });
        return data?.resolveCategoryConflicts?.success === true;
      } catch (err) {
        Sentry.captureException(err, {
          extra: { extractionId, category },
        });
        return false;
      } finally {
        setIsResolving(false);
      }
    },
    [runMutation]
  );

  return useMemo(
    () => ({ resolveCategoryConflicts, isResolving }),
    [resolveCategoryConflicts, isResolving]
  );
}
